import React, { useState, useCallback, useRef } from 'react';
import { CheckSquare, Square, Loader2 } from 'lucide-react';
import './ProcessChecklist.css';

export interface EditorChecklistItem {
    id: string;
    text: string;
    checked: boolean;
}

export interface ReviewChecklistItem {
    id: string;
    keyword: string;
    action: string;
    checked: boolean;
}

export type ChecklistItem = EditorChecklistItem | ReviewChecklistItem;

interface ProcessChecklistProps {
    checklist: ChecklistItem[];
    onUpdate: (updatedChecklist: ChecklistItem[]) => Promise<void>;
    title: string;
    isLoading?: boolean;
    type: 'editor' | 'review';
}

const ProcessChecklist: React.FC<ProcessChecklistProps> = ({
    checklist,
    onUpdate,
    title,
    isLoading = false,
    type
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [localChecklist, setLocalChecklist] = useState<ChecklistItem[]>(checklist);
    
    // Use ref to track current checklist for error recovery (avoids stale closure)
    const checklistRef = useRef<ChecklistItem[]>(checklist);

    // Update local state and ref when prop changes
    React.useEffect(() => {
        setLocalChecklist(checklist);
        checklistRef.current = checklist;
    }, [checklist]);

    const handleToggle = useCallback(async (itemId: string) => {
        const updatedChecklist = localChecklist.map(item => 
            item.id === itemId ? { ...item, checked: !item.checked } : item
        );
        
        setLocalChecklist(updatedChecklist);
        setIsSaving(true);
        
        try {
            await onUpdate(updatedChecklist);
            // Update ref on success
            checklistRef.current = updatedChecklist;
        } catch (error) {
            console.error('Error updating checklist:', error);
            // Revert on error using ref (always has current value, not stale closure)
            setLocalChecklist([...checklistRef.current]);
        } finally {
            setIsSaving(false);
        }
    }, [localChecklist, onUpdate]);

    const checkedCount = localChecklist.filter(item => item.checked).length;
    const totalCount = localChecklist.length;
    const progress = totalCount > 0 ? Math.round((checkedCount / totalCount) * 100) : 0;

    // Calculate color based on progress: red (0%) -> yellow (50%) -> green (100%)
    const getProgressColor = (percent: number): string => {
        if (percent === 0) return '#ef4444'; // red
        if (percent === 100) return '#10b981'; // green
        
        // Interpolate between red, yellow, and green
        if (percent <= 50) {
            // Red to Yellow: 0% = red, 50% = yellow
            const ratio = percent / 50;
            const r = Math.round(239 + (251 - 239) * ratio); // 239 -> 251
            const g = Math.round(68 + (191 - 68) * ratio);   // 68 -> 191
            const b = Math.round(68 + (36 - 68) * ratio);    // 68 -> 36
            return `rgb(${r}, ${g}, ${b})`;
        } else {
            // Yellow to Green: 50% = yellow, 100% = green
            const ratio = (percent - 50) / 50;
            const r = Math.round(251 - (16 - 251) * ratio);  // 251 -> 16
            const g = Math.round(191 + (185 - 191) * ratio); // 191 -> 185
            const b = Math.round(36 - (129 - 36) * ratio);  // 36 -> 129
            return `rgb(${r}, ${g}, ${b})`;
        }
    };

    const badgeColor = getProgressColor(progress);

    return (
        <div className="process-checklist-container">
            <button
                className="process-checklist-toggle"
                onClick={() => setIsOpen(!isOpen)}
                title={title}
            >
                <CheckSquare size={18} />
                <span 
                    className="process-checklist-badge"
                    style={{ backgroundColor: badgeColor }}
                >
                    {checkedCount}/{totalCount}
                </span>
                {isSaving && <Loader2 size={14} className="process-checklist-saving" />}
            </button>
            
            {isOpen && (
                <div className="process-checklist-panel">
                    <div className="process-checklist-header">
                        <h3 className="process-checklist-title">{title}</h3>
                        <button
                            className="process-checklist-close"
                            onClick={() => setIsOpen(false)}
                            title="Close"
                        >
                            ×
                        </button>
                    </div>
                    
                    <div className="process-checklist-progress">
                        <div className="process-checklist-progress-bar">
                            <div 
                                className="process-checklist-progress-fill"
                                style={{ 
                                    width: `${progress}%`,
                                    backgroundColor: badgeColor
                                }}
                            />
                        </div>
                        <span className="process-checklist-progress-text">
                            {progress}% complete
                        </span>
                    </div>
                    
                    <div className="process-checklist-list">
                        {localChecklist.map((item) => (
                            <div
                                key={item.id}
                                className={`process-checklist-item ${item.checked ? 'checked' : ''}`}
                            >
                                <button
                                    className="process-checklist-checkbox"
                                    onClick={() => handleToggle(item.id)}
                                    disabled={isSaving || isLoading}
                                >
                                    {item.checked ? (
                                        <CheckSquare size={18} className="checked-icon" />
                                    ) : (
                                        <Square size={18} className="unchecked-icon" />
                                    )}
                                </button>
                                
                                <div className="process-checklist-content">
                                    {type === 'editor' ? (
                                        <span className="process-checklist-text">
                                            {(item as EditorChecklistItem).text}
                                        </span>
                                    ) : (
                                        <div className="process-checklist-review-content">
                                            <span className="process-checklist-keyword">
                                                {(item as ReviewChecklistItem).keyword}
                                            </span>
                                            {(item as ReviewChecklistItem).action && (
                                                <span className="process-checklist-action">
                                                    {(item as ReviewChecklistItem).action}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProcessChecklist;

